import { useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import useCrmEnrichmentQuota from "app/pages/sales-intelligence/modules/quota/hooks/useCrmEnrichmentQuota";
import { setPreDefinedInvalidFields } from "app/pages/sales-intelligence/sub-modules/management/store/actions/common";
import {
    selectCurrentExportTab,
    selectCurrentInvalidPreDefined,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/common";
import type {
    selectSfAccountsColumns,
    selectSfContactsNotSelectedSettings,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforce/exportSelectors";
import type {
    ExportItem,
    ExportName,
    ExportSettingsSourceType,
    HubspotPreDefinedType,
    NewExportSetting,
} from "app/pages/sales-intelligence/sub-modules/management/store/type";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import { getExportSettingSourceType } from "app/pages/sales-intelligence/sub-modules/management/utils/utils";

import type {
    ColumnItem,
    ExcludeStatusFields,
    SimilarwebFieldConfig,
} from "app/pages/sales-intelligence/common-components/export-table/types";
import { ADD_FIELD, TECHNOGRAPHICS_FIELD } from "app/pages/sales-intelligence/common-components/export-table/const";
import {
    addItem,
    addPreDefinedCreateItem,
    addPreDefinedItem,
    addPreDefinedUpdateItem,
    addTechographicsItem,
} from "app/pages/sales-intelligence/common-components/export-table/constants";
import {
    getDefaultContactsCrmValue,
    getUniqueFieldName,
    isPreDefinedField,
} from "app/pages/sales-intelligence/common-components/export-table/utils";

import type { UseManageExportTableType } from "./type";
import { useManageExportTableProps } from "./useManageExportTableProps";
import { buildSimilarwebFieldDropdownOptions } from "app/pages/sales-intelligence/pages/manage-integration/configs/accountsFields";

import { useIsPredefinedOnCreateUpdateClaimEnabled } from "./useIsPredefinedOnCreateUpdateClaimEnabled";

export const useManageExportTable = (integrationName: Integration, tableName: ExportName): UseManageExportTableType => {
    const dispatch = useDispatch();
    const { isAvailableHubspotEnrichment, isAvailableSalesforceEnrichment } = useCrmEnrichmentQuota();

    const isPredefinedOnCreateUpdateClaimEnabled = useIsPredefinedOnCreateUpdateClaimEnabled(integrationName);

    const isHubspot = integrationName === "hubspot";
    const isSalesforce = integrationName === "salesforce";
    const isDynamics = integrationName === "dynamics";

    const isCrmEnrichmentIncluded =
        (isSalesforce && isAvailableSalesforceEnrichment) || (isHubspot && isAvailableHubspotEnrichment);

    const {
        onDelete,
        deleteLocal,
        data,
        isLoadingState,
        addSettingsThunk,
        editSettingThunk,
        isLoadingCrmFields,
        crmFields,
        filteredData,
        columnsSelectors,
        isAllColumnsLoading,
        fetchCrmFields,
    } = useManageExportTableProps(integrationName);

    const onlyTableData = useSelector(data);

    const columns = useSelector(
        (columnsSelectors as Record<ExportName, typeof selectSfAccountsColumns>)[tableName],
    ) as ColumnItem[];
    const tabName = useSelector(selectCurrentExportTab);
    const allInvalidField = useSelector(selectCurrentInvalidPreDefined(tabName));

    const isColumnsLoading = useSelector(isAllColumnsLoading);

    const deleteFromInvalid = (deletedField: ExportItem) => {
        if (isPreDefinedField(deletedField)) {
            const invalidFields = allInvalidField.filter((invalidField) => {
                if (
                    invalidField.externalField === deletedField.externalField &&
                    invalidField.field === deletedField.field
                ) {
                    return false;
                }

                return true;
            });

            dispatch(
                setPreDefinedInvalidFields({
                    tabName,
                    invalidFields,
                }),
            );
        }
    };

    const addSetting = (field: string) => dispatch(addSettingsThunk([field], tableName));
    const editSetting = (oldField: ExportItem, newField: NewExportSetting) =>
        dispatch(editSettingThunk(oldField, newField, tableName, isCrmEnrichmentIncluded));

    const deleteLocalSetting = (setting: ExportItem) => {
        deleteFromInvalid(setting);

        void dispatch(deleteLocal(setting.field));
    };
    const deleteSetting = (field: ExportItem) => {
        deleteFromInvalid(field);
        dispatch(onDelete(field));
    };

    const exportOnlyData = useMemo(() => onlyTableData.filter((item) => item.forExport !== false), [onlyTableData]);

    const tableData = useMemo(
        (): readonly ExportItem[] =>
            tableName === "companyAsLeads" || exportOnlyData.length ? [...exportOnlyData, addItem] : [],
        [exportOnlyData, tableName],
    );

    const isLoading: boolean = useSelector(isLoadingState);
    const isCrmFieldsLoading: boolean = useSelector(isLoadingCrmFields);
    const crmOptions: ExcludeStatusFields = useSelector(crmFields);

    const allSimilarwebFields: SimilarwebFieldConfig[] = useSelector(
        (filteredData as Record<ExportName, typeof selectSfContactsNotSelectedSettings>)[tableName],
    );

    const similarwebFields = useMemo(
        () => allSimilarwebFields.filter((field) => !field.isEnrichmentOnly),
        [allSimilarwebFields],
    );

    const handleDeleteClick = (deletedField: ExportItem) => {
        if (deletedField.externalField) {
            return deleteSetting(deletedField);
        }
        deleteLocalSetting(deletedField);
    };

    const onCrmCellClick = (setting: ExportItem, newSetting: { id: string; text?: string }) =>
        editSetting(setting, { externalField: newSetting.id });

    const onSimilarwebCellClick = (setting: ExportItem, newSetting: { id: string; text?: string }) => {
        if (!newSetting || !setting) {
            return;
        }

        const { field } = setting;
        const { id } = newSetting;

        const { fieldName, metadata } = getUniqueFieldName(id, tableData, field === ADD_FIELD ? undefined : setting);

        if (field === ADD_FIELD) {
            return addSetting(id);
        }

        const defaultCrmField = getDefaultContactsCrmValue(id, integrationName);

        deleteFromInvalid(setting);

        return editSetting(setting, {
            metadata,
            field: fieldName,
            externalField: defaultCrmField || "",
            sourceType: getExportSettingSourceType(metadata || id),
        });
    };

    const onToggleClick = ({ setting, key, value }: { setting: ExportItem; key: keyof ExportItem; value: boolean }) =>
        editSetting(setting, { [key]: value });

    const onExampleChange = (setting: ExportItem, value: string) => editSetting(setting, { metadata: value });

    const getCrmFields = (
        columnName: string,
        sourceType: ExportSettingsSourceType,
        isPreDefined = false,
        type: HubspotPreDefinedType,
    ) =>
        dispatch(
            fetchCrmFields({
                columnName,
                isPreDefined,
                type,
                sourceType,
            }),
        );

    const selectedCrmFields = tableData.map((record) => record.externalField);

    const focusIndexes = useMemo(() => {
        return onlyTableData.reduce<number[]>((acc, row, index) => {
            if ((!row.metadata && isPreDefinedField(row)) || !row.externalField) {
                acc.push(index);
            }
            return acc;
        }, []);
    }, [onlyTableData]);

    const fullSimilarwebFields = useMemo(() => {
        const predefinedItems = isPredefinedOnCreateUpdateClaimEnabled
            ? [addPreDefinedItem, addPreDefinedCreateItem, addPreDefinedUpdateItem]
            : [addPreDefinedItem];
        const includeTechographics = !(tableName === "contacts" && (isSalesforce || isDynamics));

        return buildSimilarwebFieldDropdownOptions({
            fields: similarwebFields,
            predefinedItems,
            techographicsItem: includeTechographics ? addTechographicsItem : undefined,
        });
    }, [tableName, isSalesforce, isDynamics, similarwebFields, isPredefinedOnCreateUpdateClaimEnabled]);

    return {
        handleDeleteClick,
        onCrmCellClick,
        onSimilarwebCellClick,
        onToggleClick,
        onExampleChange,
        getCrmFields,
        similarwebFields: fullSimilarwebFields,
        isCrmFieldsLoading,
        tableData,
        isLoading,
        crmOptions,
        columns,
        isColumnsLoading,
        focusIdx: focusIndexes[0],
        selectedCrmFields,
    };
};
